
import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { toast } from "@/hooks/use-toast";

// Define types for our auth context
interface AuthState {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook for auth provider
const useAuthProvider = (): AuthContextType => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
  });

  // Function to refresh the session
  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      setAuthState(prevState => ({
        ...prevState,
        session: data.session,
        user: data.session?.user ?? null,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error refreshing session:", error);
      setAuthState(prevState => ({ 
        ...prevState, 
        session: null,
        user: null,
        isLoading: false 
      }));
    }
  };

  // Sign out function with improved error handling
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear local state immediately
      setAuthState({
        session: null,
        user: null,
        isLoading: false,
      });
      
      toast({
        title: "Desconectado",
        description: "Você foi desconectado com sucesso."
      });
    } catch (error: any) {
      toast({
        title: "Erro ao desconectar",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth event:", event, session?.user?.id);
        
        setAuthState({
          session,
          user: session?.user ?? null,
          isLoading: false,
        });

        // Create profile for new users
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(() => {
            createUserProfile(session.user);
          }, 0);
        }
      }
    );

    // THEN get initial session
    refreshSession();

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  // Function to create user profile if it doesn't exist
  const createUserProfile = async (user: User) => {
    try {
      const { data: existingProfile, error: selectError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (selectError) {
        console.error("Error checking profile:", selectError);
        return;
      }

      if (!existingProfile) {
        const { error } = await supabase
          .from("profiles")
          .insert([
            {
              id: user.id,
              name: user.user_metadata?.name || null,
              phone: user.user_metadata?.phone || null,
            }
          ]);

        if (error) {
          console.error("Error creating profile:", error);
        } else {
          console.log("Profile created successfully for user:", user.id);
        }
      }
    } catch (error) {
      console.error("Error checking/creating profile:", error);
    }
  };

  return {
    ...authState,
    signOut,
    refreshSession,
  };
};

// Auth Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthProvider();
  
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
